<template>
    <div id="fileAddRulePanel" class="form-wrapper-process">
        <PanelDropdown v-model="folderMap" :label="$t('folderMap')" :options="folderOptions" placeholder="None"
            :formType="formType" :tooltip="tooltips.shared.folderMap"
            style="padding-left: 1rem; padding-right: 1rem;" />

        <div class="file-row-with-link">
            <FolderFileName v-model="localForm.fileName" :max-length="260" :titleLabel="$t('folderFileName')"
                :placeholder="'Enter Folder/File Name'" :tooltip="tooltips.shared.folderFileName" @validation="onValidation" />
            <div class="linked-app-container"
                :class="localForm.fileName && linkedApp && hasExtension && !isExe ? 'linked-app-container-active' : 'linked-app-container-inactive'">
                <a class="linked-app-link" href="javascript:void(0);" @click="toggleLinkedApp">
                    <i v-if="!(localForm.fileName && linkedApp && hasExtension && !isExe)"
                        class="ag-icon ag-icon_table_info small info-icon" />
                    <span class="linked-app-text">Linked File Types Policy Rule</span>
                    <i
                        :class="['pi', (!(localForm.fileName && linkedApp && hasExtension && !isExe)) ? 'pi-chevron-down' : (showLinkedApp ? 'pi-chevron-up' : 'pi-chevron-down'), 'chevron-icon']"></i>
                </a>
            </div>
        </div>
        <div v-if="showLinkedApp && hasExtension" class="linked-app-card ">
            <i class="ag-icon ag-icon_table_info_filled small info-icon-linked-app" />
            <div class="row-line" style="border-bottom: 1px solid #DBE2E9;">
                <span class="meta-label col-3">Type of file:</span>
                <span class="meta-value col-9">{{ matchedPolicy?.fileTypePolicyName || fallbackFileTypeName || '-'
                    }}</span>
            </div>
            <div class="row-line" style="border-bottom: 1px solid #DBE2E9;"
                :style="!matchedPolicy ? 'opacity: 50%;' : ''">
                <span class="meta-label col-3">Opens with:</span>
                <span class="meta-value col-9"> {{ matchedPolicy ? formattedApps : '-' }}</span>
            </div>
            <div class="row-line" :style="!matchedPolicy ? 'opacity: 50%;' : ''">
                <span class="meta-label col-3">Access Control:</span>
                <span class="meta-value col-9">{{ matchedPolicy?.driverActionOverride || '-' }}</span>
            </div>
            <div class="file-type-warning" v-if="!matchedPolicy && hasExtension">
                <span class="">There is no file types policy defined for this extension. Click edit to configure.</span>
            </div>
            <div class="card-actions">
                <Button :class="matchedPolicy ? 'edit-btn' : 'edit-btn-two'" icon="pi pi-chevron-right small"
                    iconPos="right" label="Edit" @click="onEditLinkedApp" type="button"></Button>
            </div>
        </div>
        <PanelDropdown v-model="localForm.accessControl" :label="$t('accessControl')" :options="accessControlOptions"
            placeholder="Select" :formType="formType" :tooltip="tooltips.process.accessControl"
            style="padding-top: 10px; padding-left: 1rem; padding-right: 1rem;" />
        <FeatureToggle class="feature-toggle-div" v-model="localForm.applicationExecutionControl" type="application"
            :label="$t('executableControl')" :form="localForm" :formType="formType">
        </FeatureToggle>
        <FeatureToggle v-model="localForm.moduleControl" type="module" :label="$t('scriptModuleControl')"
            :form="localForm" :formType="formType">
        </FeatureToggle>
        <ExpertNotes v-model="localForm.expertNotes" :max-length="1000" :label="$t('notes')"
            style="padding-left: 1rem; padding-right: 1rem;" />
    </div>
    <ag-loading v-if="loading" />
</template>

<script setup>
import { defineProps, ref, defineEmits, computed, onMounted } from 'vue'
import FeatureToggle from './components/featureToggle.vue'
import PanelInput from './components/panelInput.vue'
import PanelDropdown from './components/panelDropdown.vue'
import Button from 'primevue/button'
import { useStore } from "@/store";
import FileTypePolicyForm from '@/components/ag-edit-panel/forms/file-type-policy-form.vue'
import { GeneralActionTypes } from "@/store/modules/general/action-types";
import { getFriendlyNameForExtension } from '@/helpers/constants/fileTypeExtensions';
import ExpertNotes from './components/expertNotes.vue';
import tooltips from '@/assets/tooltips/en.json'
import { useRoute } from "vue-router";
import FolderFileName from './components/folderFileName.vue';
const props = defineProps({
    form: {
        type: Object,
        required: true,
    },
    formType: {
        type: String,
        default: 'process'
    },
    enumKey: {
        type: String,
        default: 'AccessControl'
    },
    linkedApp: {
        type: Boolean,
        default: false
    }
})
const store = useStore();
const emit = defineEmits(['update:form', 'edit', 'validation'])
const folderOptions = computed(() => {
    const original = store.state.general.folderMaps || [];
    return [{ label: "None", value: null }, { label: "Any", value: -1 }, ...original];
});
const accessControlOptions = computed(() => {
    return store.state.general.enums[props.enumKey] || [];
});
const fileTypePolicyList = computed(() => {
    return store.state.general.fileTypesPolicies || [];
});
const localForm = computed({
    get() {
        return props.form;
    },
    set(newVal) {
        emit('update:form', newVal);
    }
});

const folderMap = computed({
    get() {
        return localForm.value.isFolderMapIdAny ? -1 : localForm.value.folderMapId;
    },
    set(val) {
        if (val === -1) {
            localForm.value.isFolderMapIdAny = true;
            localForm.value.folderMapId = -1;
        } else if (val === null) {
            localForm.value.isFolderMapIdAny = false;
            localForm.value.folderMapId = null;
        } else {
            localForm.value.isFolderMapIdAny = false;
            localForm.value.folderMapId = val;
        }
    },
});
const templateId = ref(0);
const templatePolicy = ref(false);
onMounted(() => {
    const templateStr = localStorage.getItem("agms_template");
    const mainId = templateStr ? JSON.parse(templateStr)?.mainid : null;
    templateId.value = mainId;
    const routeName = useRoute().name;
    if (routeName == 'templateFileTypesPolicy')
        templatePolicy.value = true;
});

const showLinkedApp = ref(false);
function toggleLinkedApp() { 
    if(isExe.value){
        showLinkedApp.value= false;
    }else{
        showLinkedApp.value = !showLinkedApp.value;
    }
}
function onEditLinkedApp() {
    if (matchedPolicy.value?.id) {
        getFileTypesPolicyDetail(matchedPolicy.value.id)
    } else {
        emit('edit', {
            title: 'Add File Type Policy',
            formComponent: FileTypePolicyForm,
            formType: 'fileTypePolicy',
            formModel: { name: '', fileExtensionDaos: [], fileExtensionApps: [] },
            ruleId: localForm.value.id ?? 0,
            previousFormModel: { ...(localForm.value || {}) }
        })
    }
}

const fileExt = computed(() => {
    const name = (localForm.value.fileName || '').trim();
    const m = name.match(/\.([A-Za-z0-9]+)$/);
    return m ? '.' + m[1].toLowerCase() : '';
});
const hasExtension = computed(() => !!fileExt.value);
const isExe = computed(() => fileExt.value === '.exe');
const fallbackFileTypeName = computed(() => fileExt.value ? getFriendlyNameForExtension(fileExt.value) : '-');
const matchedPolicy = computed(() => {
    if (!fileExt.value) return null;

    return fileTypePolicyList.value.find(
        policy => policy.fileExtension.toLowerCase() === fileExt.value
    ) || null;
});
const formattedApps = computed(() => {
    if (!matchedPolicy.value) return 'None selected';
    if (Array.isArray(matchedPolicy.value.fileExtensionApps) && matchedPolicy.value.fileExtensionApps.length > 0) {
        return matchedPolicy.value.fileExtensionApps
            .map(app => app.applicationPath)
            .join(', ');
    }
    return 'None selected';
});
const getFileTypesPolicyDetail = async (fileTypesPolicyId) => {
    try {
        const response = templatePolicy.value
            ? await store.dispatch(GeneralActionTypes.GET_FILE_TYPES_POLICY_DETAIL_TEMPLATE, fileTypesPolicyId)
            : await store.dispatch(GeneralActionTypes.GET_FILE_TYPES_POLICY_DETAIL, fileTypesPolicyId);
        if (response.isSuccess) {
            emit('edit', {
                title: 'Edit File Type Policy',
                formComponent: FileTypePolicyForm,
                formType: 'fileTypePolicy',
                formModel: templatePolicy.value ? response.entityData.templatePolicyViewModel.appGroup : response.entityData,
                ruleId: localForm.value.id ?? 0,
                previousFormModel: { ...(localForm.value || {}) }
            })
        }

    } catch (error) {
        console.error(error);
    }
};
function onValidation(valid){
    emit('validation', valid)
}
</script>
<style>
.accordion-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding-right: 2rem;
}

.form-wrapper-process {
    font-size: 0.8rem !important;
}

.custom-input {
    width: inherit;
}

.label-check {
    margin-left: 5px;
    margin-bottom: 0px !important;
}

.form-label {
    color: #164B6C;
    font-weight: bold;
    margin-left: 8px;
}

.info-icon-linked-app {
    margin-left: 3px;
    position: absolute;
    top: 8px;
    right: 20px;
    color: #164b6c;
}

.feature-toggle-div {
    margin-top: 10px;
}

.file-row-with-link {
    position: relative;
    padding-left: 1rem;
    padding-right: 1rem;
}

.linked-app-container {
    display: flex;
    margin-top: 4px;
    padding: 10px 20px;
    width: 100%;
}

.linked-app-container-active {
    color: #0055A6;
    background-color: #EEF7FF;
}

.linked-app-container-inactive {
    background-color: #ECECEC;
    color: #C4C4C4;
}

.linked-app-container-active .linked-app-link {
    color: #164b6c;
}

.linked-app-container-inactive .linked-app-link {
    color: #C4C4C4 !important;
}

.linked-app-link {
    font-size: 0.7rem;
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 4px;
    text-decoration: none;
    width: 100%;
}

.linked-app-link,
.linked-app-link:hover,
.linked-app-link:focus,
.linked-app-link:active {
    text-decoration: none !important;
    outline: none;
}

.linked-app-text {
    text-decoration: underline;
}

.chevron-icon {
    text-decoration: none !important;
}

.linked-app-card {
    position: relative;
    background: #eef7ff;
    border-radius: 2px;
    padding: 12px 20px;
    margin: 0 1rem;
    padding-top: 20px;
}

.linked-app-card::before {
    content: "";
    position: absolute;
    left: 20px;
    right: 20px;
    top: 0;
    height: 1px;
    background-color: #D6D6D6;
}

.row-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.meta-label {
    color: #01172F;
    font-weight: bold;
    padding-left: 0;
}

.meta-value {
    color: #111;
}

.card-actions {
    text-align: right;
}

.edit-btn {
    padding: 0px 10px;
    background-color: #f7fbff;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    font-size: 0.8rem;
    color: #5d5f62;
}

.edit-btn-two {
    padding: 0px 10px;
    background-color: #F7FBFF;
    border-radius: 6px;
    border: 1px solid #3377B8;
    font-size: 0.8rem;
    color: #3377B8;
}

.p-button-label {
    font-weight: 400 !important;
}

.chevron-icon {
    margin-left: auto;
    text-decoration: none !important;
}

.file-type-warning {
    border: 1px solid #FAC812;
    padding: 4px 10px;
    background-color: #FFF9E7;
    color: #000000;
    border-radius: 4px;
    font-size: 12px;
    margin-bottom: 16px;
}
</style>
